import path from 'path';

import { OffsetPagedResults } from '~interfaces';
import { BasePagedParameters, GeneralPagedParameters } from '~interfaces/requests';
import { PagedResponse } from '~interfaces/responses';
import { TopologyService } from '~services';

import PaymentStatusEnum from '../enums/paymentStatusEnum';
import Billing from '../interfaces/billing';

class BillingsService extends TopologyService {
	public readonly path = 'finances/billings';

	public readonly usersSubPath = 'users';
	public readonly bookingsSubPath = 'bookings';

	constructor() {
		super('v1');
	}

	/**
	 * Get a list of access rules
	 * @param date As date object or date iso string
	 * TODO: date should eventually be of type Date, but for backwards compatibility
	 * allow a string.
	 * @returns
	 */
	async getBillings(args: {
		// TODO: the providerId is used in the admin panel, but does it do anything.
		// Seems not defined in the swagger
		providerId?: string;
		// TODO: check if we really need to provide the organisationId. Feels random...
		organisationId?: string;
		date: Date | string;
		paymentStatus?: PaymentStatusEnum;
		searchQuery?: string;
		excludeOneOffPayments?: boolean;
	}): Promise<Billing> {
		const pathParam = typeof args.date === 'string' ? args.date : args.date.toISOString();
		const { data } = await this._client.get<BillingResponse>(path.join(this.path, pathParam), {
			params: {
				providerId: args.providerId,
				organisationId: args.organisationId,
				filters: args.paymentStatus,
				searchTerm: args.searchQuery,
				excludeOneOffPayments: args.excludeOneOffPayments,
			},
		});

		return BillingsService.fromResponse(data);
	}

	/**
	 * Get personal billings
	 * @param args
	 */
	async getBillingsUnmapped(
		args: BasePagedParameters & {
			searchTerm?: string;
			excludeOneOffPayments?: boolean;
			includeCurrent?: boolean;
			userId?: number;
		}
	): Promise<BillingResponse> {
		const { data } = await this._client.get<BillingResponse>(this.path, {
			params: {
				pageNumber: args.page,
				pageSize: args.pageSize,
				organisationId: args.organisationId,
				excludeOneOffPayments: args.excludeOneOffPayments,
				userId: args.userId,
				includeCurrent: args.includeCurrent,
			},
		});

		return data;
	}

	/**
	 * Get personal billings
	 * @param args
	 */
	async getBillingBookingsUnmapped(
		date: string,
		userId: string,
		args: GeneralPagedParameters<string> & {
			excludeOneOffPayments?: boolean;
			includeCurrent?: boolean;
			userId?: number;
			providerId?: number;
		}
	): Promise<OffsetPagedResults<unknown>> {
		const { page, searchQuery, descending, ...restArgs } = args;
		const { data } = await this._client.get<PagedResponse<unknown>>(
			path.join(this.path, date, this.usersSubPath, userId, this.bookingsSubPath),
			{
				params: {
					...restArgs,
					searchTerm: searchQuery,
					pageNumber: page,
					orderDescending: descending,
				},
			}
		);

		return this.mapPagedResponse(data, (el) => el);
	}

	/**
	 * TODO: don't mapping for now, because we use this service mainly for "legacy" code.
	 * Eventually do proper mapping to seperate business and api data
	 * @param data
	 * @returns
	 */
	static fromResponse(data: BillingResponse): Billing {
		const { ...rest } = data;

		return {
			...rest,
		};
	}
}

interface BillingResponse {
	start: string;
	end: string;
	title: string;
	open: number;
	status: string;
	amount: {
		currencySymbol: string;
		subtotal: number;
		discount: number;
		refund: number;
		priceVat: number;
		totalPrice: number;
	};
}

export default BillingsService;
